import type { FlowSchedule } from "@cohesive/api-client";
import {
  generateCronExpression,
  parseCronExpression,
  to12HourFormat,
} from "@cohesive/ui-components";
import { format, isSameYear } from "date-fns";

/** Active = the scheduler will fire it; paused = next_run_at cleared. */
export type AutomationState = "active" | "paused";

export type CadenceKind =
  | "daily"
  | "weekly"
  | "monthly"
  | "annually"
  | "custom"
  | "once"
  | "none";

export interface CadenceInfo {
  state: AutomationState;
  kind: CadenceKind;
  /** Pure cadence line ("Daily 9:30am", "Once Apr 23", "Custom") or null when unscheduled. */
  cadence: string | null;
  /** Row meta for flat contexts: cadence, falling back to "Paused". */
  short: string;
  /** Fuller pane line ("Weekly on Mon, Wed at 9:30am (America/Chicago)"); null for custom/none. */
  long: string | null;
  /** True when the cron can't be represented by the structured editor. */
  isCustomCron: boolean;
}

const DAY_NAMES_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_NAMES_PLURAL = [
  "Sundays",
  "Mondays",
  "Tuesdays",
  "Wednesdays",
  "Thursdays",
  "Fridays",
  "Saturdays",
];
const MONTH_NAMES_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function isIntInRange(value: string, min: number, max: number): boolean {
  if (!/^\d{1,2}$/.test(value)) return false;
  const n = Number(value);
  return n >= min && n <= max;
}

/** Comma list of plain integers ("1,15"), each in range. */
function isIntList(value: string, min: number, max: number): boolean {
  return value.split(",").every((part) => isIntInRange(part, min, max));
}

/** Comma list allowing ranges ("1-3,5"), each bound in range. */
function isIntListOrRanges(value: string, min: number, max: number): boolean {
  return value.split(",").every((part) => {
    if (part.includes("-")) {
      const [start, end] = part.split("-");
      return (
        start !== undefined &&
        end !== undefined &&
        isIntInRange(start, min, max) &&
        isIntInRange(end, min, max) &&
        Number(start) <= Number(end)
      );
    }
    return isIntInRange(part, min, max);
  });
}

/**
 * True when the cron fits one of the structured editor's four shapes
 * (daily / weekly / monthly / annually with a single fire time). Everything
 * else — steps, wildcards in minute/hour, mixed day-of-month+day-of-week —
 * is "custom" and shown read-only, because `parseCronExpression` mis-parses
 * shapes outside these four (e.g. hourly `0 * * * *`).
 */
export function isStructuredCron(cron: string): boolean {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return false;
  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts as [
    string,
    string,
    string,
    string,
    string,
  ];

  if (!isIntInRange(minute, 0, 59) || !isIntInRange(hour, 0, 23)) return false;

  const anyDom = dayOfMonth === "*";
  const anyMonth = month === "*";
  const anyDow = dayOfWeek === "*";

  // Daily
  if (anyDom && anyMonth && anyDow) return true;
  // Weekly (ranges allowed — parse expands them)
  if (anyDom && anyMonth && isIntListOrRanges(dayOfWeek, 0, 6)) return true;
  // Monthly
  if (!anyDom && anyMonth && anyDow && isIntList(dayOfMonth, 1, 31))
    return true;
  // Annually
  if (
    !anyDom &&
    !anyMonth &&
    anyDow &&
    isIntList(dayOfMonth, 1, 31) &&
    isIntInRange(month, 1, 12)
  ) {
    return true;
  }
  return false;
}

/** "09:30" → "9:30am"; "09:00" → "9am" (design-system time register). */
export function formatTime24(time24: string | undefined): string {
  if (!time24) return "";
  return to12HourFormat(time24)
    .replace(" ", "")
    .replace(":00", "")
    .toLowerCase();
}

/** "Apr 23" / "Apr 23, 2027" (year only when not the current year, unless forced). */
export function formatDay(
  iso: string,
  opts?: { alwaysYear?: boolean },
): string {
  const date = new Date(iso);
  const withYear = opts?.alwaysYear || !isSameYear(date, new Date());
  return format(date, withYear ? "MMM d, yyyy" : "MMM d");
}

/** "Apr 23, 9:30am" in the viewer's local time. */
export function formatDayTime(iso: string): string {
  const date = new Date(iso);
  const time = format(date, "h:mmaaa").replace(":00", "");
  return `${formatDay(iso)}, ${time}`;
}

function ordinal(n: number): string {
  const rem10 = n % 10;
  const rem100 = n % 100;
  if (rem10 === 1 && rem100 !== 11) return `${n}st`;
  if (rem10 === 2 && rem100 !== 12) return `${n}nd`;
  if (rem10 === 3 && rem100 !== 13) return `${n}rd`;
  return `${n}th`;
}

function describeWeekdays(days: number[]): { compact: string; full: string } {
  const sorted = [...new Set(days)].sort((a, b) => a - b);
  if (sorted.length >= 7) return { compact: "Daily", full: "every day" };
  if (sorted.length === 5 && sorted.join(",") === "1,2,3,4,5") {
    return { compact: "Weekdays", full: "weekdays" };
  }
  if (sorted.length === 1) {
    const day = sorted[0]!;
    return {
      compact: DAY_NAMES_PLURAL[day] ?? "Weekly",
      full: DAY_NAMES_PLURAL[day] ?? "weekly",
    };
  }
  const names = sorted.map((d) => DAY_NAMES_SHORT[d] ?? "?");
  return { compact: "Weekly", full: names.join(", ") };
}

const BROWSER_TIME_ZONE = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
})();

/** Appended to `long` only when the schedule's zone differs from the viewer's. */
function timeZoneSuffix(timeZone: string | undefined): string {
  if (!timeZone || timeZone === BROWSER_TIME_ZONE) return "";
  return ` (${timeZone})`;
}

export function describeCadence(
  schedule: Pick<FlowSchedule, "cron" | "cron_time_zone" | "next_run_at">,
): CadenceInfo {
  const state: AutomationState = schedule.next_run_at ? "active" : "paused";
  const tz = timeZoneSuffix(schedule.cron_time_zone);

  const finish = (
    kind: CadenceKind,
    cadence: string | null,
    long: string | null,
    isCustomCron = false,
  ): CadenceInfo => ({
    state,
    kind,
    cadence,
    short: cadence ?? (state === "paused" ? "Paused" : "Scheduled"),
    long,
    isCustomCron,
  });

  if (schedule.cron) {
    if (!isStructuredCron(schedule.cron)) {
      return finish("custom", "Custom", null, true);
    }

    const parsed = parseCronExpression(schedule.cron);
    const time = formatTime24(parsed.time24);

    switch (parsed.scheduleType) {
      case "weekly": {
        const days = (parsed.daysOfWeek ?? []).map(Number);
        const { compact, full } = describeWeekdays(days);
        return finish(
          "weekly",
          `${compact} ${time}`,
          full === "every day"
            ? `Every day at ${time}${tz}`
            : `Weekly on ${full} at ${time}${tz}`,
        );
      }
      case "monthly": {
        const days = (parsed.daysOfMonth ?? []).map(Number);
        const dayList = days.map(ordinal).join(", ");
        return finish(
          "monthly",
          `Monthly ${time}`,
          `Monthly on the ${dayList} at ${time}${tz}`,
        );
      }
      case "annually": {
        const monthIdx = Number(parsed.month ?? "1") - 1;
        const monthName = MONTH_NAMES_SHORT[monthIdx] ?? "";
        const day = Number(parsed.daysOfMonth?.[0] ?? "1");
        return finish(
          "annually",
          `Yearly ${time}`,
          `Yearly on ${monthName} ${day} at ${time}${tz}`,
        );
      }
      case "daily":
      default:
        return finish("daily", `Daily ${time}`, `Daily at ${time}${tz}`);
    }
  }

  if (schedule.next_run_at) {
    return finish(
      "once",
      `Once ${formatDay(schedule.next_run_at)}`,
      `Runs once on ${formatDayTime(schedule.next_run_at)}`,
    );
  }

  return finish("none", null, null);
}

/**
 * Structured-editor state → PATCH payload pieces. Thin wrapper so components
 * share one code path with the round-trip guard's expectations.
 */
export function buildCron(
  parsed: ReturnType<typeof parseCronExpression>,
): string {
  return generateCronExpression(parsed);
}
